import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Save,
  XCircle,
  UserCircle,
  KeyRound,
  Info,
  ShieldCheck,
} from "lucide-react";

// Component Imports
import MainLayout from "../../../layout";
import FormTextField from "../../../components/form/text";
import { FormImageField } from "../../../components/form/image";
import { FormTextAreaField } from "../../../components/form/text-area";
import ResetPasswordModal from "../../../components/modal/reset-password-modal";
import Loader from "../../../assets/icons/loader/loader";
import { url } from "../../../config";
import successToast from "../../../components/toast/successToast";
import errorToast from "../../../components/toast/errorToast";

const EditUsers = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState();
  const [openResetPassword, setOpenResetPassword] = useState(false);

  const initialValues = {
    id: params?.id,
    profile_picture: data?.profile_picture ?? "",
    email: data?.email ?? "",
    name: data?.name ?? "",
    description: data?.description ?? "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().required("Required").email("Invalid email"),
  });

  const fetchData = () => {
    setLoading(true);
    axios
      .post(`${url}/ffadmin/read`, { id: params?.id })
      .then((res) => {
        setData(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("API Error:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [params.id]);

  const handleSubmit = (values) => {
    axios
      .post(`${url}/ffadmin/edit`, values)
      .then((response) => {
        successToast("User updated successfully");
        navigate(`/admin/users/list`);
      })
      .catch((error) => {
        errorToast(error?.response?.data?.message || "Update failed");
      });
  };

  if (loading)
    return (
      <MainLayout>
        <div className="h-full flex flex-col items-center justify-center bg-[#F8FAFC]">
          <div className="h-20 w-20">
            <Loader />
          </div>
          <p className="text-slate-400 font-medium animate-pulse mt-4">
            Loading user profile...
          </p>
        </div>
      </MainLayout>
    );

  return (
    <MainLayout>
      <ResetPasswordModal
        id={params?.id}
        isOpen={openResetPassword}
        onClose={() => setOpenResetPassword(false)}
      />

      <div className="flex flex-col h-full bg-[#F8FAFC]">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting }) => (
            <Form className="flex flex-col h-full">
              {/* --- Sticky Header --- */}
              <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => navigate("/admin/users/list")}
                    className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-600"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div>
                    <h1 className="text-xl font-bold text-slate-800">
                      Edit Admin Profile
                    </h1>
                    <p className="text-xs text-slate-500 font-medium tracking-wide">
                      ID: {params?.id}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setOpenResetPassword(true)}
                    className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-blue-600 rounded-xl font-bold text-sm hover:bg-blue-50 transition-all shadow-sm"
                  >
                    <KeyRound size={16} /> Reset Password
                  </button>
                  <button
                    onClick={() => navigate("/admin/users/list")}
                    type="button"
                    className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                    title="Cancel"
                  >
                    <XCircle size={24} />
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-50"
                  >
                    <Save size={18} />{" "}
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </header>

              {/* --- Main Content --- */}
              <div className="flex-1 overflow-auto p-4 sm:p-4 custom-scrollbar">
                <div className="max-w-6xl mx-auto">
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-6">
                    <div className="space-y-4 w-full max-w-[550px]">
                      <div className="mb-4">
                        <FormImageField
                          name={"profile_picture"}
                          label={"Profile Image"}
                        />
                      </div>
                      <div className="my-4">
                        <FormTextField
                          name={"name"}
                          label={"Full Name"}
                          placeholder={`Enter user's full name`}
                        />
                      </div>

                      <FormTextField
                        name={"email"}
                        label={"Email Address"}
                        placeholder={`example@pos.com`}
                      />

                      <div className="md:col-span-2">
                        <FormTextAreaField
                          name={"description"}
                          label={"Biography / Internal Notes"}
                          placeholder={`Brief details about the user's role or department...`}
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </MainLayout>
  );
};

export default EditUsers;
